// src/components/WeatherAlerts.jsx
import React, { useState, useEffect } from 'react';
import { AlertTriangle, CloudRain, Thermometer, Info, X } from 'lucide-react';
import { db } from '../utils/firebase';
import { doc, getDoc } from 'firebase/firestore';

const WeatherAlerts = ({ clubId }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dismissedAlerts, setDismissedAlerts] = useState({});

  // Load dismissed alerts from localStorage (club-specific)
  useEffect(() => {
    if (!clubId) return;
    
    const storageKey = `dismissedWeatherAlerts_${clubId}`;
    const dismissed = JSON.parse(localStorage.getItem(storageKey) || '{}');
    
    // Clean up expired dismissals (older than 24 hours)
    const now = Date.now();
    const validDismissals = {};
    
    Object.entries(dismissed).forEach(([alertId, dismissTime]) => {
      const hoursSinceDismiss = (now - dismissTime) / (1000 * 60 * 60);
      if (hoursSinceDismiss < 24) {
        validDismissals[alertId] = dismissTime;
      }
    });
    
    // Save cleaned up dismissals back to localStorage
    if (Object.keys(validDismissals).length !== Object.keys(dismissed).length) {
      localStorage.setItem(storageKey, JSON.stringify(validDismissals));
    }
    setDismissedAlerts(validDismissals);
  }, [clubId]);

  useEffect(() => {
    if (!clubId) return;
    fetchWeatherAndGenerateAlerts();
  }, [clubId]);

  // Convert lat/lng to geohash for BOM API
  const encodeGeohash = (latitude, longitude, precision = 7) => {
    const BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz';
    let idx = 0;
    let bit = 0;
    let evenBit = true;
    let geohash = '';
    let latMin = -90, latMax = 90;
    let lonMin = -180, lonMax = 180;

    while (geohash.length < precision) {
      if (evenBit) {
        const lonMid = (lonMin + lonMax) / 2;
        if (longitude > lonMid) {
          idx = (idx << 1) + 1;
          lonMin = lonMid;
        } else {
          idx = idx << 1;
          lonMax = lonMid;
        }
      } else {
        const latMid = (latMin + latMax) / 2;
        if (latitude > latMid) {
          idx = (idx << 1) + 1;
          latMin = latMid;
        } else {
          idx = idx << 1;
          latMax = latMid;
        }
      }
      evenBit = !evenBit;

      if (++bit === 5) {
        geohash += BASE32[idx];
        bit = 0;
        idx = 0;
      }
    }
    return geohash;
  };

  const fetchWeatherAndGenerateAlerts = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get club location from Firebase
      const clubDoc = await getDoc(doc(db, 'clubs', clubId));
      
      if (!clubDoc.exists()) {
        throw new Error('Club not found');
      }

      const clubData = clubDoc.data();
      
      // Check multiple possible location formats
      let location = null;
      
      // Format 1: satelliteConfig.location (new format)
      if (clubData.satelliteConfig?.location?.lat && clubData.satelliteConfig?.location?.lng) {
        location = clubData.satelliteConfig.location;
        console.log('Found location in satelliteConfig.location:', location);
      }
      // Format 2: Direct location object (fallback)
      else if (clubData.location?.lat && clubData.location?.lng) {
        location = clubData.location;
        console.log('Found location in clubData.location:', location);
      }
      // Format 3: Lat/lng at root level (fallback)
      else if (clubData.lat && clubData.lng) {
        location = { lat: clubData.lat, lng: clubData.lng };
        console.log('Found location at root level:', location);
      }

      if (!location) {
        console.warn('No location data found for club - weather alerts disabled');
        setAlerts([]);
        setLoading(false);
        return;
      }

      console.log('Using club location:', location);

      // Convert to geohash for BOM API
      const geohash = encodeGeohash(location.lat, location.lng);
      console.log('Geohash:', geohash);

      // Fetch weather forecast from BOM API
      const forecastUrl = `https://api.weather.bom.gov.au/v1/locations/${geohash}/forecasts/daily`;
      
      const response = await fetch(forecastUrl);
      
      if (!response.ok) {
        throw new Error(`BOM API error: ${response.status}`);
      }

      const weatherData = await response.json();
      console.log('Weather data received:', weatherData);

      // Analyze forecast and generate alerts
      const generatedAlerts = analyzeWeatherData(weatherData);
      setAlerts(generatedAlerts);

    } catch (err) {
      console.error('Error fetching weather:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const analyzeWeatherData = (weatherData) => {
    const alerts = [];
    
    if (!weatherData || !weatherData.data) {
      console.log('No weather data or data array');
      return alerts;
    }

    const forecasts = weatherData.data;
    console.log('Analyzing forecasts, count:', forecasts.length);
    
    // Check each day's forecast
    for (let i = 0; i < Math.min(forecasts.length, 7); i++) {
      const day = forecasts[i];
      const date = day.date;
      const rain = day.rain?.amount?.max || 0;
      const temp = day.temp_max || 0;
      
      console.log(`Day ${i}: Date=${date}, Rain=${rain}mm, Temp=${temp}°C`);

      // Check for extreme heat (34°C+)
      if (temp >= 34) {
        alerts.push({
          type: 'extreme-heat',
          severity: 'critical',
          date: date,
          title: `Extreme Heat Alert - ${formatDate(date)}`,
          message: `Temperature forecast: ${temp}°C. Strong recommendation to cancel all outdoor activities. Review federation extreme weather policy.`,
          icon: 'thermometer',
          color: '#dc2626' // red-600
        });
      }
      // Check for high heat (30-33°C)
      else if (temp >= 30) {
        alerts.push({
          type: 'high-heat',
          severity: 'warning',
          date: date,
          title: `High Heat Warning - ${formatDate(date)}`,
          message: `Temperature forecast: ${temp}°C. Consider rescheduling activities to later evening hours in line with federation extreme weather policy.`,
          icon: 'thermometer',
          color: '#ea580c' // orange-600
        });
      }

      // Check for heavy rain (20mm+)
      if (rain >= 20) {
        alerts.push({
          type: 'heavy-rain',
          severity: 'high',
          date: date,
          title: `Heavy Rain Alert - ${formatDate(date)}`,
          message: `${rain}mm of rain forecast. Review all allocations and consider pitch conditions. May need to reschedule.`,
          icon: 'cloud-rain',
          color: '#2563eb' // blue-600
        });
      }
      // Check for moderate rain (10-19mm)
      else if (rain >= 10) {
        alerts.push({
          type: 'moderate-rain',
          severity: 'medium',
          date: date,
          title: `Moderate Rain Alert - ${formatDate(date)}`,
          message: `${rain}mm of rain forecast. Monitor pitch conditions for potential impact.`,
          icon: 'cloud-rain',
          color: '#0891b2' // cyan-600
        });
      }
    }

    // Check for consecutive rain days
    checkConsecutiveRainDays(forecasts, alerts);

    console.log('Generated alerts:', alerts.length, alerts);
    return alerts;
  };

  const checkConsecutiveRainDays = (forecasts, alerts) => {
    for (let i = 0; i < Math.min(forecasts.length - 1, 6); i++) {
      const day1 = forecasts[i];
      const day2 = forecasts[i + 1];
      
      const rain1 = day1.rain?.amount?.max || 0;
      const rain2 = day2.rain?.amount?.max || 0;

      // 20mm+ rain for 2 consecutive days - CRITICAL
      if (rain1 >= 20 && rain2 >= 20) {
        alerts.push({
          type: 'consecutive-heavy-rain',
          severity: 'critical',
          date: `${day1.date} - ${day2.date}`,
          title: `Critical: Heavy Rain Multiple Days`,
          message: `${rain1}mm and ${rain2}mm forecast on consecutive days (${formatDate(day1.date)} - ${formatDate(day2.date)}). Highly recommend reviewing all allocations. Pitch conditions will be severely impacted.`,
          icon: 'cloud-rain',
          color: '#dc2626' // red-600
        });
      }
      // 10mm+ rain for 2 consecutive days - HIGH
      else if (rain1 >= 10 && rain2 >= 10) {
        alerts.push({
          type: 'consecutive-moderate-rain',
          severity: 'high',
          date: `${day1.date} - ${day2.date}`,
          title: `Sustained Rain Alert`,
          message: `${rain1}mm and ${rain2}mm forecast on consecutive days (${formatDate(day1.date)} - ${formatDate(day2.date)}). Review allocations and monitor pitch conditions closely.`,
          icon: 'cloud-rain',
          color: '#ea580c' // orange-600
        });
      }
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const handleDismiss = (alertId) => {
    if (!clubId) return;
    
    const now = Date.now();
    const updatedDismissals = {
      ...dismissedAlerts,
      [alertId]: now
    };
    
    // Save to localStorage with club-specific key
    const storageKey = `dismissedWeatherAlerts_${clubId}`;
    localStorage.setItem(storageKey, JSON.stringify(updatedDismissals));
    setDismissedAlerts(updatedDismissals);
  };

  const isAlertDismissed = (alertId) => {
    const dismissTime = dismissedAlerts[alertId];
    if (!dismissTime) return false;
    
    const hoursSinceDismiss = (Date.now() - dismissTime) / (1000 * 60 * 60);
    return hoursSinceDismiss < 24;
  };

  const getAlertIcon = (iconType) => {
    switch (iconType) {
      case 'thermometer':
        return <Thermometer size={20} />;
      case 'cloud-rain':
        return <CloudRain size={20} />;
      default:
        return <AlertTriangle size={20} />;
    }
  };

  const getSeverityStyles = (severity) => {
    switch (severity) {
      case 'critical':
        return {
          bg: '#fee2e2',
          border: '#dc2626',
          text: '#7f1d1d'
        };
      case 'high':
        return {
          bg: '#ffedd5',
          border: '#ea580c',
          text: '#7c2d12'
        };
      case 'warning':
        return {
          bg: '#fef3c7',
          border: '#f59e0b',
          text: '#78350f'
        };
      case 'medium':
        return {
          bg: '#dbeafe',
          border: '#3b82f6',
          text: '#1e3a8a'
        };
      default:
        return {
          bg: '#f3f4f6',
          border: '#9ca3af',
          text: '#1f2937'
        };
    }
  };

  if (loading) {
    return (
      <div style={{
        padding: '16px',
        backgroundColor: '#f9fafb',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        marginBottom: '16px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#6b7280',
          fontSize: '14px'
        }}>
          <div style={{
            width: '16px',
            height: '16px',
            border: '2px solid #e5e7eb',
            borderTop: '2px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          Loading weather alerts...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '12px',
        backgroundColor: '#fef3c7',
        borderRadius: '8px',
        border: '1px solid #fbbf24',
        marginBottom: '16px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#78350f',
          fontSize: '14px'
        }}>
          <Info size={16} />
          Weather alerts unavailable
        </div>
      </div>
    );
  }

  if (alerts.length === 0) {
    return null; // No alerts to show
  }

  // Filter out dismissed alerts
  const visibleAlerts = alerts.filter(alert => {
    const alertId = `${alert.type}-${alert.date}`;
    const isDismissed = !isAlertDismissed(alertId);
    if (!isDismissed) {
      console.log(`Alert filtered out (dismissed): ${alertId}`);
    }
    return isDismissed;
  });
  
  console.log(`Total alerts: ${alerts.length}, Visible alerts: ${visibleAlerts.length}`);

  if (visibleAlerts.length === 0) {
    return null; // All alerts dismissed
  }

  return (
    <div style={{ marginBottom: '12px' }}>
      {visibleAlerts.map((alert, index) => {
        const styles = getSeverityStyles(alert.severity);
        const alertId = `${alert.type}-${alert.date}`;
        
        return (
          <div
            key={`${alert.type}-${alert.date}-${index}`}
            style={{
              padding: '10px',
              backgroundColor: styles.bg,
              border: `2px solid ${styles.border}`,
              borderRadius: '8px',
              marginBottom: '10px',
              animation: 'slideIn 0.3s ease-out'
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px'
            }}>
              <div style={{ 
                color: styles.border,
                flexShrink: 0,
                marginTop: '1px'
              }}>
                {getAlertIcon(alert.icon)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontWeight: '600',
                  fontSize: '12px',
                  color: styles.text,
                  marginBottom: '5px',
                  letterSpacing: '0.2px',
                  lineHeight: '1.3'
                }}>
                  {alert.title}
                </div>
                <div style={{
                  fontSize: '11px',
                  color: styles.text,
                  lineHeight: '1.4',
                  marginBottom: '8px',
                  opacity: 0.95
                }}>
                  {alert.message}
                </div>
                
                {/* Acknowledge Button */}
                <button
                  onClick={() => handleDismiss(alertId)}
                  style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    border: `1px solid ${styles.border}`,
                    borderRadius: '4px',
                    padding: '5px 8px',
                    color: styles.text,
                    fontSize: '10px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <X size={10} />
                  Acknowledge (24h)
                </button>
              </div>
            </div>
          </div>
        );
      })}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default WeatherAlerts;
